import * as d3 from 'd3';
import ChartComponent from './ChartComponent';
import React from 'react';
import "./barchart.scss";

const GroupedBarChart = ({
    data,
    width,
    height,
    margin = { top: 10, right: 10, bottom: 20, left: 40 },
    xPadding = 0.1,
    yPadding = 0.05,
    colors = d3.schemeSet2,
    hoverColors,
    xAxisLabel,
    yAxisLabel,
    showTooltip = true,
    onMouseOver = () => { },
    onMouseOut = () => { },
    defaultYAxis = 100,
    additionalYTooltipLabel,
    maxBarWidth,
    boxWidth,
    restrictYAxisToWholeNumbers = true
}) => {
    function draw(chart) {
        const containerWidth = boxWidth || width;
        // Add background rectangle with border
        chart.append("rect")
            .attr("x", margin.left)
            .attr("y", margin.top)
            .attr("width", containerWidth - margin.left - margin.right)
            .attr("height", height - margin.top - margin.bottom)
            .attr("fill", "none")
            .attr("stroke", "#e0e0e0")
            .attr("stroke-width", 1);

        // Compute values
        const X = d3.map(data || [{ x: "" }], d => d.x);
        const Y = d3.map(data || [{ y: defaultYAxis }], d => d.y);

        // Check if Z domain exists in data
        const hasZDomain = data && data.length > 0 && data[0].z !== undefined;
        const Z = hasZDomain ? d3.map(data || [], d => d.z) : d3.map(data || [], () => 'default');

        // Compute default domains, and unique the x-domain
        const xDomain = new d3.InternSet(X);
        const yDomain = [0, d3.max(Y)];
        const zDomain = new d3.InternSet(Z);

        const I = d3.range(X.length).filter(i => xDomain.has(X[i]));

        const xScale = d3.scaleBand(xDomain, [margin.left, width - margin.right]).padding(hasZDomain ? xPadding : 0);
        const zDomainSize = zDomain.size;
        const effectivePadding = hasZDomain ? yPadding : 0;
        const maxGroupWidth = maxBarWidth ? (maxBarWidth * zDomainSize) / (1 - effectivePadding) : xScale.bandwidth();
        const groupWidth = Math.min(xScale.bandwidth(), maxGroupWidth);
        const xzScale = d3.scaleBand(zDomain, [0, groupWidth]).padding(effectivePadding);
        const groupOffset = (xScale.bandwidth() - groupWidth) / 2;
        const yScale = d3.scaleLinear(yDomain, [height - margin.bottom, margin.top]);
        const zScale = d3.scaleOrdinal(zDomain, colors);

        const xAxis = d3.axisBottom(xScale)
            .tickSizeOuter(0)
            .tickSize(0)
            .tickFormat(d => {
                const avgCharWidth = 8; // approximate width of a character in pixels
                const maxChars = Math.floor(xScale.bandwidth() / avgCharWidth);
                return d.length > maxChars ? d.substring(0, maxChars - 3) + '...' : d;
            });
        const yAxis = d3.axisLeft(yScale)
            .tickSize(0);

        if (restrictYAxisToWholeNumbers) {
            yAxis.tickValues(yScale.ticks(height / 60).filter(Number.isInteger))
                .tickFormat(d3.format("d"));
        } else {
            yAxis.ticks(height / 60);
        }

        const xAxisG = chart.append("g")
            .attr("transform", `translate(0,${height - margin.bottom})`)
            .call(xAxis)
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick text")
                .style("font-size", "14px")
                .style("font-weight", "500")
            );

        const labelTooltip = chart.append("foreignObject")
            .attr("class", "x-label-tooltip")
            .style("display", "none")
            .style("pointer-events", "none")
            .attr("width", 200)
            .attr("height", 300);

        labelTooltip.raise();

        const tooltipDiv = labelTooltip.append("xhtml:div")
            .style("background", "#333")
            .style("color", "white")
            .style("padding", "6px 10px")
            .style("border-radius", "6px")
            .style("font-size", "12px")
            .style("display", "inline-block")
            .style("white-space", "normal")
            .style("max-width", "180px")
            .style("line-height", "16px")
            .style("position", "relative")
            .style("box-shadow", "0px 2px 6px rgba(0,0,0,0.3)")

        tooltipDiv.append("xhtml:div")
            .style("position", "absolute")
            .style("left", "50%")
            .style("bottom", "-6px")
            .style("transform", "translateX(-50%)")
            .style("width", "0")
            .style("height", "0")
            .style("border-left", "6px solid transparent")
            .style("border-right", "6px solid transparent")
            .style("border-top", "6px solid #333");

        xAxisG.selectAll(".tick text").each(function (d) {
            const fullText = d;
            const displayedText = this.textContent;

            if (fullText !== displayedText) {
                d3.select(this)
                    .style("cursor", "pointer")
                    .on("mouseover", (event) => {
                        tooltipDiv.text(fullText);

                        // Re-append the pointer since .text() clears content
                        tooltipDiv.append("xhtml:div")
                            .style("position", "absolute")
                            .style("left", "50%")
                            .style("bottom", "-6px")
                            .style("transform", "translateX(-50%)")
                            .style("width", "0")
                            .style("height", "0")
                            .style("border-left", "6px solid transparent")
                            .style("border-right", "6px solid transparent")
                            .style("border-top", "6px solid #333");

                        labelTooltip.style("display", "");

                        // Get text box dimensions after rendering
                        const bbox = tooltipDiv.node().getBoundingClientRect();
                        const tooltipWidth = bbox.width;
                        const tooltipHeight = bbox.height;

                        const [x, y] = d3.pointer(event, chart.node());

                        labelTooltip
                            .attr("x", x - tooltipWidth / 2)
                            .attr("y", y - tooltipHeight - 20); // Position above cursor

                        labelTooltip.raise(); // keep above bars
                    })

                    .on("mouseout", () => {
                        labelTooltip.style("display", "none");
                    });
            }
        });



        // Draw Y Axis
        chart.append("g")
            .attr("transform", `translate(${margin.left},0)`)
            .call(yAxis)
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick text")
                .style("font-size", "14px")
                .style("font-weight", "500"))
            .call(g => g.selectAll(".tick line").clone()
                .attr("x2", containerWidth - margin.left - margin.right)
                .attr("stroke-opacity", 0.1));

        // Add axis labels if provided
        if (xAxisLabel) {
            chart.append("text")
                .attr("text-anchor", "middle")
                .attr("x", width / 2)
                .attr("y", height - 5)
                .text(xAxisLabel);
        }

        if (yAxisLabel) {
            chart.append("text")
                .attr("text-anchor", "middle")
                .attr("transform", "rotate(-90)")
                .attr("x", -height / 2)
                .attr("y", 15)
                .text(yAxisLabel);
        }

        // Bars + Hover Tooltip
        let tooltipContainer;

        if (data) {
            tooltipContainer = chart.append("g")
                .attr("class", "tooltip-container")
                .style("display", "none")
                .style("pointer-events", "none");

            // Calculate effective bar width
            const baseBarWidth = hasZDomain ? xzScale.bandwidth() : xScale.bandwidth();
            const effectiveBarWidth = maxBarWidth ? Math.min(baseBarWidth, maxBarWidth) : baseBarWidth;
            const barOffset = (baseBarWidth - effectiveBarWidth) / 2;

            chart.append("g")
                .selectAll("rect")
                .data(I)
                .join("rect")
                .attr("x", i => xScale(X[i]) + (hasZDomain ? groupOffset : 0) + (!hasZDomain ? barOffset : xzScale(Z[i])))
                .attr("y", i => yScale(Y[i]))
                .attr("width", effectiveBarWidth)
                .attr("height", i => yScale(0) - yScale(Y[i]))
                .attr("fill", i => hasZDomain ? ((colors && colors[Z[i]]) || zScale(Z[i])) : colors)
                .attr("opacity", 1)
                .on("mouseover", (event, i) => {
                    const rect = event.currentTarget;
                    const tooltipData = { x: X[i], y: Y[i], z: Z[i] };

                    d3.select(rect)
                        .transition()
                        .duration(200)
                        .attr("fill", hasZDomain ? (hoverColors && hoverColors[Z[i]]) || zScale(Z[i]) : hoverColors || colors)
                        .attr("opacity", 0.5);

                    if (showTooltip) {
                        const tooltipWidth = 150;
                        const tooltipHeight = 80;
                        let xPos = +rect.getAttribute("x");
                        let yPos = +rect.getAttribute("y");

                        // Calculate initial position (above and aligned with bar)
                        let adjustedXPos = xPos;
                        let adjustedYPos = yPos - tooltipHeight - 5; // 5px gap above bar

                        // Check right boundary
                        if (adjustedXPos + tooltipWidth > containerWidth - margin.right) {
                            adjustedXPos = containerWidth - margin.right - tooltipWidth;
                        }

                        // Check left boundary
                        if (adjustedXPos < margin.left) {
                            adjustedXPos = margin.left;
                        }

                        // Check top boundary - if tooltip goes above chart, show it below the bar instead
                        if (adjustedYPos < margin.top) {
                            adjustedYPos = yPos + (yScale(0) - yPos) + 5; // Position below bar with 5px gap

                            // If still doesn't fit below, position it at the top margin
                            if (adjustedYPos + tooltipHeight > height - margin.bottom) {
                                adjustedYPos = margin.top;
                            }
                        }

                        // Check bottom boundary
                        if (adjustedYPos + tooltipHeight > height - margin.bottom) {
                            adjustedYPos = height - margin.bottom - tooltipHeight;
                        }

                        tooltipContainer
                            .style("display", null)
                            .raise()
                            .attr("transform", `translate(${adjustedXPos},${adjustedYPos})`);

                        tooltipContainer.selectAll("*").remove();

                        tooltipContainer
                            .append("foreignObject")
                            .attr("width", tooltipWidth)
                            .attr("height", tooltipHeight)
                            .append("xhtml:div")
                            .attr("class", "ui tiny segment compact")
                            .style("font-size", "12px")
                            .style("padding", "8px")
                            .style("margin", "0")
                            .html(`
                                <div class="ui statistic BarChart__tooltip">
                                    <div class="label BarChart__tooltip-label">${hasZDomain ? tooltipData.z : tooltipData.x}</div>
                                    <div class="value BarChart__tooltip-value">${tooltipData.y}${additionalYTooltipLabel ? ` ${additionalYTooltipLabel}` : ""}</div>
                                </div>
                            `);
                    }

                    onMouseOver(tooltipData);
                })
                .on("mouseout", (event) => {
                    if (showTooltip) tooltipContainer.style("display", "none");

                    d3.select(event.currentTarget)
                        .transition()
                        .duration(200)
                        .attr("fill", i => hasZDomain ? (colors && colors[Z[i]]) || zScale(Z[i]) : colors)
                        .attr("opacity", 1);

                    onMouseOut();
                });
        }
    }

    return <ChartComponent draw={draw} />;
}

export default GroupedBarChart;
